/**
 * Cadence + period selector shell around the shared {@link Report}.
 *
 * `ReportWithPeriodControls` adds the two date dropdowns described in the
 * product spec on top of the pure {@link Report} presenter:
 *
 *  - **Cadence** — Daily / Weekly / Monthly (defaults to Weekly).
 *  - **Period**  — the concrete window within that cadence, newest-first
 *                  (e.g. "14 Sep 2026", "7 Sep 2026", …), defaulting to the
 *                  most recent (index 0).
 *
 * It owns the selection state only; all window/cadence maths lives in the pure
 * {@link buildPeriodOptions} (so it stays unit-testable), and all rendering
 * (KPIs, graphs, title, reporting-period chrome) stays in {@link Report}. The
 * selectors are passed into `Report`'s `controls` slot — docked in the report
 * header rather than floating above the surface — so they stay put across the
 * loading skeleton, error, unavailable, and loaded states. The selected
 * option's `{ start, stop, frequency }` is handed to `Report`, whose title and
 * period block already re-derive from the window, so changing either dropdown
 * updates the graph granularity, the KPIs (recomputed server-side over the new
 * window), and the document header automatically.
 *
 * Graph bucketing is fixed per cadence (daily → hourly, weekly/monthly → daily)
 * and is therefore not a user choice — see {@link buildPeriodOptions}.
 */
import { useMemo, useState } from "react"
import { Flex, NativeSelectField, NativeSelectRoot, Text } from "@chakra-ui/react"
import Report from "./Report"
import type { ReportScopeInput } from "./useReport"
import { CADENCE_OPTIONS, DEFAULT_CADENCE, buildPeriodOptions, type Cadence, type PeriodOption } from "./periodOptions"

export interface ReportWithPeriodControlsProps {
  /** Entity kind the report targets (enum value or lowercase string). */
  scope: ReportScopeInput
  /** Identifier of the entity (asset / hybrid / system / installation). */
  entityId: string
  /**
   * Human name of the entity, used to build the header title ("Garic SES
   * performance report"). When omitted the title falls back to the scope-based
   * form ("Asset performance report").
   */
  entityName?: string
  /**
   * The most-recent instant the report should reach — the asset's assignment
   * end (when returned/deactivated) or the system's latest end. When omitted the
   * options anchor to now (the latest period). ISO 8601.
   */
  anchorEnd?: string
  /**
   * The earliest instant the period options may reach — the entity's
   * assignment/system start. When omitted, only each cadence's item limit caps
   * the list. ISO 8601.
   */
  lowerBound?: string
  /**
   * Opaque share token for the login-free public view. Forwarded to {@link Report}
   * so the report is fetched via `getPublicReport` (token-scoped) and the
   * operator Share action is hidden. The authed views leave this unset.
   */
  shareToken?: string
}

/**
 * The two compact, inline selectors handed to {@link Report}'s `controls` slot.
 *
 * Kept deliberately minimal — auto-width native selects wrapped in inline copy
 * that reads as a sentence ("Showing [Weekly] data for [14 Sep 2026]"), so each
 * dropdown is labelled by its surrounding words rather than a stacked form
 * label. This is the streamlined design: the selectors read as a small header
 * toolbar rather than a labelled form block above the report.
 */
function PeriodControls({
  cadence,
  onCadenceChange,
  options,
  selectedId,
  onPeriodChange,
}: {
  cadence: Cadence
  onCadenceChange: (cadence: Cadence) => void
  options: PeriodOption[]
  selectedId?: string
  onPeriodChange: (id: string) => void
}) {
  return (
    <Flex gap={2} align="center" wrap="wrap" width={{ base: "100%", md: "auto" }}>
      {/* Labels read as a sentence around the two selects:
          "Showing [Weekly] data for [14 Sep 2026]". */}
      <Text fontSize="sm" color="#545454" flexShrink={0}>
        Showing
      </Text>
      <NativeSelectRoot size="sm" width="auto" minWidth="7rem">
        <NativeSelectField
          value={cadence}
          onChange={(event) => onCadenceChange(event.target.value as Cadence)}
          aria-label="Cadence"
        >
          {CADENCE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </NativeSelectField>
      </NativeSelectRoot>
      <Text fontSize="sm" color="#545454" flexShrink={0}>
        data for
      </Text>
      <NativeSelectRoot size="sm" width="auto" minWidth="8.5rem">
        <NativeSelectField value={selectedId ?? ""} onChange={(event) => onPeriodChange(event.target.value)} aria-label="Period">
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </NativeSelectField>
      </NativeSelectRoot>
    </Flex>
  )
}

/**
 * Render the shared performance report with cadence + period dropdowns docked
 * in its header (via {@link Report}'s `controls` slot).
 *
 * Selection state: `cadence` (default weekly) and `periodId` (default the first,
 * i.e. most-recent, option). Changing cadence rebuilds the period list; the
 * selected period is resolved against that fresh list and falls back to the
 * newest option whenever the previously-selected id is absent (e.g. after a
 * cadence switch), so the period dropdown never lands on a stale/empty value.
 *
 * The selectors are passed to `Report` rather than rendered above it, so they
 * live inside the report surface and stay put across the loading skeleton,
 * error, unavailable, and loaded states.
 */
export function ReportWithPeriodControls({ scope, entityId, entityName, anchorEnd, lowerBound, shareToken }: ReportWithPeriodControlsProps) {
  const [cadence, setCadence] = useState<Cadence>(DEFAULT_CADENCE)
  const [periodId, setPeriodId] = useState<string | undefined>(undefined)

  // Memoise the anchor/lower-bound Dates so the option list (and thus the
  // resolved window) is stable across renders. A fresh `new Date()` each render
  // would churn the options and, downstream, the report's query variables.
  const anchorDate = useMemo(() => (anchorEnd ? new Date(anchorEnd) : new Date()), [anchorEnd])
  const lowerDate = useMemo(() => (lowerBound ? new Date(lowerBound) : undefined), [lowerBound])

  const options = useMemo(() => buildPeriodOptions(cadence, anchorDate, lowerDate), [cadence, anchorDate, lowerDate])

  // Resolve the selected option: the chosen id when it still exists in the
  // current list, otherwise the newest option (index 0). This handles the
  // initial render (periodId undefined) and cadence switches (old id absent).
  const selected = options.find((option) => option.id === periodId) ?? options[0]

  const controls = (
    <PeriodControls
      cadence={cadence}
      onCadenceChange={(next) => {
        setCadence(next)
        // Reset to the newest period of the new cadence.
        setPeriodId(undefined)
      }}
      options={options}
      selectedId={selected?.id}
      onPeriodChange={setPeriodId}
    />
  )

  // The cadence label ("Weekly") leads the report's period metadata line, so the
  // cadence reads there rather than in the title.
  const cadenceLabel = CADENCE_OPTIONS.find((option) => option.value === cadence)?.label

  // The PDF download (container ref, chart rasterisation, and the download
  // button) lives inside Report, where the resolved report data (KPIs, graphs)
  // and the header formatters are — so it only needs the filename inputs here.
  return (
    <Report
      scope={scope}
      entityId={entityId}
      entityName={entityName}
      cadenceLabel={cadenceLabel}
      cadence={cadence}
      start={selected?.start}
      stop={selected?.stop}
      frequency={selected?.frequency}
      controls={controls}
      enablePdfDownload
      shareToken={shareToken}
    />
  )
}

export default ReportWithPeriodControls
